import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { DashboardLayout } from '@/components/Dashboard/DashboardLayout';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/apiClient';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Truck, ArrowLeft, Edit, Trash2, Ban, CheckCircle, Plus,
  Building2, User, Mail, Phone, MapPin, FileText, CreditCard,
  Calendar, Receipt, Loader2, Save
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { SupplierFormDialog, type Supplier } from '@/components/Suppliers/SupplierFormDialog';
import type { AccountingAccount } from '@/types/billing';

export default function SupplierDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, userRole } = useAuth();
  const queryClient = useQueryClient();

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [notesChanged, setNotesChanged] = useState(false);

  const canEdit = ['superadmin', 'admin', 'supervisor'].includes(userRole || '');

  const { data: supplier, isLoading, error } = useQuery<Supplier>({
    queryKey: ['supplier', id],
    queryFn: async () => {
      const { data, error } = await api
        .from('billing_customers')
        .select('*')
        .eq('id', id)
        .single();
      if (error) throw error;
      setNotes(data.notes || '');
      return data as Supplier;
    },
    enabled: !!id,
  });

  const { data: expenseAccount } = useQuery<AccountingAccount | null>({
    queryKey: ['expense-account', supplier?.default_expense_account_id],
    queryFn: async () => {
      if (!supplier?.default_expense_account_id) return null;
      const { data, error } = await api
        .from('accounting_accounts')
        .select('*')
        .eq('id', supplier.default_expense_account_id)
        .single();
      if (error) return null;
      return data;
    },
    enabled: !!supplier?.default_expense_account_id,
  });

  const { data: recentOrders = [] } = useQuery({
    queryKey: ['supplier-orders', id],
    queryFn: async () => {
      const { data, error } = await api
        .from('supplier_orders')
        .select('id, order_number, order_date, status, total_amount')
        .eq('supplier_id', id)
        .order('order_date', { ascending: false })
        .limit(5);
      if (error) return [];
      return data || [];
    },
    enabled: !!id,
  });

  const toggleActiveMutation = useMutation({
    mutationFn: async () => {
      const { error } = await api
        .from('billing_customers')
        .update({
          is_active: !supplier?.is_active,
          updated_at: new Date().toISOString(),
          updated_by: user?.id,
        })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supplier', id] });
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      toast.success(supplier?.is_active ? 'Proveedor inhabilitado' : 'Proveedor habilitado');
    },
    onError: (error: any) => {
      toast.error(`Error: ${error.message}`);
    },
  });

  const saveNotesMutation = useMutation({
    mutationFn: async () => {
      const { error } = await api
        .from('billing_customers')
        .update({
          notes,
          updated_at: new Date().toISOString(),
          updated_by: user?.id,
        })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supplier', id] });
      setNotesChanged(false);
      toast.success('Notas guardadas');
    },
    onError: (error: any) => {
      toast.error(`Error: ${error.message}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const { error } = await api
        .from('billing_customers')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      toast.success('Proveedor eliminado');
      navigate('/billing/suppliers');
    },
    onError: (error: any) => {
      toast.error(`Error: ${error.message}`);
    },
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  if (error || !supplier) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <p className="text-muted-foreground">Proveedor no encontrado</p>
          <Button variant="outline" asChild>
            <Link to="/billing/suppliers"><ArrowLeft className="w-4 h-4 mr-2" />Volver</Link>
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/billing/suppliers"><ArrowLeft className="w-4 h-4" /></Link>
            </Button>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center">
                {supplier.customer_type === 'company' ? (
                  <Building2 className="w-8 h-8 text-orange-600" />
                ) : (
                  <User className="w-8 h-8 text-orange-600" />
                )}
              </div>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-3">
                  {supplier.legal_name}
                  {supplier.is_blocked ? (
                    <Badge variant="destructive">Bloqueado</Badge>
                  ) : supplier.is_active ? (
                    <Badge className="bg-green-100 text-green-700">Activo</Badge>
                  ) : (
                    <Badge variant="secondary">Inactivo</Badge>
                  )}
                </h1>
                {supplier.trade_name && (
                  <p className="text-muted-foreground">{supplier.trade_name}</p>
                )}
                {supplier.entity_type === 'both' && (
                  <Badge variant="outline" className="mt-1">Cliente y Proveedor</Badge>
                )}
              </div>
            </div>
          </div>

          {canEdit && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditDialogOpen(true)}>
                <Edit className="w-4 h-4 mr-2" />
                Editar
              </Button>
              <Button
                variant="outline"
                onClick={() => toggleActiveMutation.mutate()}
                disabled={toggleActiveMutation.isPending}
              >
                {supplier.is_active ? (
                  <>
                    <Ban className="w-4 h-4 mr-2" />
                    Inhabilitar
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Habilitar
                  </>
                )}
              </Button>
              <Button
                variant="destructive"
                onClick={() => setDeleteDialogOpen(true)}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Eliminar
              </Button>
              <Button asChild>
                <Link to={`/billing/supplier-orders?create=true&supplier_id=${supplier.id}`}>
                  <Plus className="w-4 h-4 mr-2" />
                  Crear Pedido
                </Link>
              </Button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Info Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* General Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Informacion General
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Tipo</p>
                  <p className="font-medium">
                    {supplier.customer_type === 'company' ? 'Empresa' : 'Autonomo'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">NIF/CIF</p>
                  <p className="font-medium font-mono">{supplier.tax_id || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Codigo Interno</p>
                  <p className="font-medium">{supplier.customer_code || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Relacion</p>
                  <p className="font-medium">
                    {supplier.entity_type === 'both' ? 'Cliente y Proveedor' : 'Solo Proveedor'}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Contact Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Phone className="w-5 h-5" />
                  Contacto
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {supplier.contact_name && (
                  <div className="flex items-center gap-3">
                    <User className="w-4 h-4 text-muted-foreground" />
                    <span>{supplier.contact_name}</span>
                  </div>
                )}
                {supplier.email && (
                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <a href={`mailto:${supplier.email}`} className="text-primary hover:underline">
                      {supplier.email}
                    </a>
                  </div>
                )}
                {supplier.phone && (
                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <a href={`tel:${supplier.phone}`} className="hover:underline">
                      {supplier.phone}
                    </a>
                  </div>
                )}
                {(supplier.address_line1 || supplier.city) && (
                  <div className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 text-muted-foreground mt-0.5" />
                    <div>
                      {supplier.address_line1 && <p>{supplier.address_line1}</p>}
                      <p>
                        {[supplier.postal_code, supplier.city, supplier.province]
                          .filter(Boolean)
                          .join(', ')}
                      </p>
                    </div>
                  </div>
                )}
                {!supplier.contact_name && !supplier.email && !supplier.phone && !supplier.address_line1 && (
                  <p className="text-muted-foreground">Sin datos de contacto</p>
                )}
              </CardContent>
            </Card>

            {/* Notes */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Notas
                  </span>
                  {notesChanged && canEdit && (
                    <Button
                      size="sm"
                      onClick={() => saveNotesMutation.mutate()}
                      disabled={saveNotesMutation.isPending}
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Guardar
                    </Button>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={notes}
                  onChange={(e) => {
                    setNotes(e.target.value);
                    setNotesChanged(true);
                  }}
                  placeholder="Escribe notas sobre este proveedor..."
                  rows={4}
                  disabled={!canEdit}
                />
              </CardContent>
            </Card>
          </div>

          {/* Side Column */}
          <div className="space-y-6">
            {/* Accounting */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Contabilidad
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground">Saldo Pendiente</p>
                  <p className={`text-2xl font-bold ${(supplier.current_balance || 0) > 0 ? 'text-amber-600' : ''}`}>
                    {formatCurrency(supplier.current_balance || 0)}
                  </p>
                </div>
                <Separator />
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Dias de pago</p>
                    <p className="font-medium">{supplier.default_payment_terms_days || 30} dias</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Limite credito</p>
                    <p className="font-medium">{formatCurrency(supplier.credit_limit || 0)}</p>
                  </div>
                </div>
                {expenseAccount && (
                  <div className="text-sm">
                    <p className="text-muted-foreground">Cuenta de gasto</p>
                    <p className="font-medium font-mono">
                      {expenseAccount.code} - {expenseAccount.name}
                    </p>
                  </div>
                )}
                {supplier.bank_account_iban && (
                  <div className="text-sm">
                    <p className="text-muted-foreground">IBAN</p>
                    <p className="font-medium font-mono text-xs break-all">
                      {supplier.bank_account_iban}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Orders */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Receipt className="w-5 h-5" />
                  Ultimos Pedidos
                </CardTitle>
              </CardHeader>
              <CardContent>
                {recentOrders.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No hay pedidos</p>
                ) : (
                  <div className="space-y-3">
                    {recentOrders.map((order: any) => (
                      <div key={order.id} className="flex items-center justify-between text-sm">
                        <div>
                          <p className="font-medium">#{order.order_number}</p>
                          <p className="text-xs text-muted-foreground">
                            {order.order_date && format(new Date(order.order_date), 'dd/MM/yyyy', { locale: es })}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">{formatCurrency(order.total_amount)}</p>
                          <Badge variant="outline" className="text-xs">
                            {order.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Metadata */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4" />
                  Informacion del Registro
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-2">
                {supplier.created_at && (
                  <div>
                    <p className="text-muted-foreground">Creado</p>
                    <p>{format(new Date(supplier.created_at), "dd/MM/yyyy HH:mm", { locale: es })}</p>
                  </div>
                )}
                {supplier.updated_at && (
                  <div>
                    <p className="text-muted-foreground">Ultima actualizacion</p>
                    <p>{format(new Date(supplier.updated_at), "dd/MM/yyyy HH:mm", { locale: es })}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Edit Dialog */}
      <SupplierFormDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        supplier={supplier}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['supplier', id] });
        }}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar Proveedor</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estas seguro de que deseas eliminar a <strong>{supplier.legal_name}</strong>?
              Esta accion no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteMutation.mutate()}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
